import { CognitoIdentityServiceProvider } from 'aws-sdk';
import React, { createContext, useContext, useState } from 'react';
import { decodeToken } from 'react-jwt';
import { useConfigContext } from './config-context';
import { AuthContextType, UserAuthenticated } from './types';

const AuthContext = createContext<AuthContextType>({
  cognitoServiceProvider: new CognitoIdentityServiceProvider(),
  authStatus: UserAuthenticated.UNKNOWN,
  tokenStore: { accessToken: '', idToken: '', refreshToken: '' },
  userLocation: '',
  email: '',
  updateEmail: () => {},
  updateAuthStatus: () => {},
  updateTokenStore: () => {},
  updateRefreshTokenCookie: (_: string) => {},
});

const AuthProvider = ({ children }: { children: any }) => {
  const {
    auth: { region },
  } = useConfigContext();

  const [authStatus, updateAuthStatus] = useState(UserAuthenticated.UNKNOWN);
  const [tokenStore, updateTokenStore] = useState({
    accessToken: '',
    idToken: '',
    refreshToken: '',
  });
  const [email, updateEmail] = useState<string>('');

  const updateRefreshTokenCookie = (refreshToken: string) => {
    function setCookie(
      name: string,
      value: string,
      options: { [key: string]: string | boolean | Date } = {},
    ) {
      const cookieOptions = Object.entries(options)
        .map(([key, val]) => `${key}=${val}`)
        .join('; ');
      document.cookie = `${name}=${value}; ${cookieOptions}`;
    }

    const hoursToMilliseconds = (hours: number) => hours * 60 * 60 * 1000;
    setCookie('refreshToken', refreshToken, {
      path: '/',
      expires: new Date(Date.now() + hoursToMilliseconds(8)),
      secure: true,
      sameSite: 'None',
    });
  };

  const cognitoServiceProvider = new CognitoIdentityServiceProvider({
    region,
  });

  const userLocation =
    decodeToken<{ 'custom:location': string }>(tokenStore.idToken)?.['custom:location'] ?? '';

  return (
    <AuthContext.Provider
      value={{
        cognitoServiceProvider,
        authStatus,
        updateAuthStatus,
        tokenStore,
        userLocation,
        updateTokenStore,
        email,
        updateEmail,
        updateRefreshTokenCookie,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuthContext = () => useContext(AuthContext);

export { AuthContext, AuthProvider, useAuthContext };
